import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoadingSpinner } from '@shared-ui/components/LoadingSpinner';
import { isFeatureEnabled } from '@shared-ui/feature-gates';
import { rememberReturnTo } from '../../utils/returnTo';

export function ProtectedRoute() {
  const { isAuthenticated, isLoading, needsRegistration, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner fullScreen message="Loading..." />;
  }

  if (!isAuthenticated) {
    // Router state carries only the pathname and dies on a reload, so the whole
    // destination — query included — is stored where sign-in can still find it
    // after an OAuth or OTP round trip (CP-2).
    rememberReturnTo(`${location.pathname}${location.search}`);
    return <Navigate to={`/login${location.search}`} state={{ from: location }} replace />;
  }

  // Chapter Practice is exempt: `/practice/:slug` is public and already shows
  // the chapter to a signed-in student whose profile is incomplete, so its
  // one-by-one view (`/practice/:slug/q/:n`) must not be the one click that
  // lands them on a grade-and-exam form instead (CP-3). Registration is
  // still asked the moment they step into the full app.
  if (
    needsRegistration &&
    location.pathname !== '/complete-registration' &&
    !location.pathname.startsWith('/practice/')
  ) {
    return <Navigate to="/complete-registration" replace />;
  }

  // Day-1 first-run: route a user OWED the FRE (`frePending`, set at account
  // creation, cleared once shown) into the flow. Existing/legacy users never have
  // the flag → never routed here (no activity heuristic, no backfill). The server
  // resolve is still authoritative. See docs/FeatureDesigns/first-run/lifecycle-stage1-2-first-run-habit.md
  // Battle routes are legit deep-link / notification / refresh destinations
  // (e.g. the friend-accept link /battle/friends?accept=…), so don't bounce
  // them through first-run — that fires on the next Home visit anyway.
  if (
    !needsRegistration &&
    user?.frePending &&
    isFeatureEnabled('day_one_first_run') &&
    location.pathname !== '/first-run' &&
    !location.pathname.startsWith('/battle') &&
    // …nor Chapter Practice: a student who followed a link from a video asked
    // for one chapter's questions, and the FRE would put an exam-and-class
    // picker in front of them — one that does not offer their class (CP-3).
    !location.pathname.startsWith('/practice/')
  ) {
    return <Navigate to="/first-run" replace />;
  }

  return <Outlet />;
}
